import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";

import { useProtectedAxiosInstance } from "../../../hooks/axiosInstance";
import CustomTable from "../../common/tables/Table";

import { useNotificationContext } from "../../../context/notificationContext";
import errorResolver from "../../../utils/error-resolver";

import {
  FilterOperatorEnum,
  SortDirectionEnum,
} from "../../../services/Schemas/ListQueryString";

import EditAction from "../../common/edit-action/EditAction";

import { useUrl } from "../../../hooks/urlEncoding";
import { routerPaths } from "../../../constants/Paths";

import { AccessTokenSchema } from "../../../services/Schemas/AccessToken";
import {
  AccessTokenListColumnFilterable,
  AccessTokenListColumnSortable,
  AccessTokensListRequest,
} from "../../../services/Endpoints/access-tokens/AccessTokensEndpoint";
import { WorkspaceSchema } from "../../../services/Schemas/Workspace";
import { PartnerSchema } from "../../../services/Schemas/Partner";
import { dateFormatter } from "../../../utils/date-formatter";
import UserLink from "../common/UserLink";
import {
  WorkspaceListColumnFilterable,
  WorkspaceListColumnSortable,
} from "../../../services/Endpoints/workspaces";
import {
  PartnerListColumnFilterable,
  PartnerListColumnSortable,
} from "../../../services/Endpoints/partners/PartnerEndpoint";
import { AccessEnums } from "../../../constants/accessEnum";
import { useCanAccess } from "../../../hooks/canAccess";
import { ITableSchema } from "../../../utils/types";
import DateTimeFilter from "../../common/tables/filters/DateTimeFilter";
import NumberFilter from "../../common/tables/filters/NumberFilter";
import { Link, useLocation } from "react-router-dom";
import useTableSchema from "../../../hooks/useTableSchema";
import { DocumentText, Trash } from "iconsax-react";
import LinkWithPopover from "../common/LinkWithPopover";
import {
  LogListColumnFilterable,
  LogListColumnSortable,
} from "../../../services/Endpoints/logs/LogListEndpoints";
import useUrlSort from "../../../hooks/useUrlSort";
import { Key } from "iconsax-react";
import { PbxServerSchema } from "../../../services/Schemas/PbxServer";
export interface IUsers {
  id: number;
  workspace: null | WorkspaceSchema | ReactNode;
  pbx_server_id: PbxServerSchema | ReactNode;
  expired_at: string;
  created_at: string;

  operations: ReactNode | string;
}

export interface ISupportTabsComponent {
  walletReload: boolean;
  setWalletReload: (payload: boolean) => void;
  setTokenInfo?: (payload: AccessTokenSchema) => void;
  setOpenModal: (payload: boolean) => void;
  setOpenShowTokenModal?: React.Dispatch<React.SetStateAction<boolean>>;
  setTokenId?: React.Dispatch<React.SetStateAction<number>>;
}

export default function AccessTokenTableComponent({
  walletReload,
  setTokenInfo,
  setTokenId,
  setOpenShowTokenModal,
  setOpenModal,
  setWalletReload,
}: Readonly<ISupportTabsComponent>) {
  const { queryStringUrl, parseQueryString, setParamWithSearchParams } =
    useUrl();
  const parsed = parseQueryString();
  const initialPage = parsed.page ? Number(parsed.page) : 1;
  const initialRows = parsed.count ? Number(parsed.count) : 10;
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);
  const [dataTransaction, setDataTransaction] = useState<IUsers[] | []>([]);

  const [total, setTotal] = useState(0);
  const [waiting, setWaiting] = useState(false);

  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();
  const { can } = useCanAccess();
  const location = useLocation();
  const { parseUrlSorts, applyUrlSorts } = useUrlSort();
  const sorts = parseUrlSorts();
  const handleView = (payload: AccessTokenSchema) => {
    setOpenModal(true);
    setTokenInfo && setTokenInfo(payload);
  };

  const handleDelete = async (id: number) => {
    const confirmDelete = window.confirm("آیا از حذف این توکن مطمئن هستید؟");
    if (!confirmDelete) return;

    try {
      setWaiting(true);
      const request = new AccessTokensListRequest(protectedInstance);
      await request.remove(id.toString());

      setNotification({
        type: "success",
        message: "توکن با موفقیت حذف شد.",
      });

      setWalletReload(!walletReload);
    } catch (error) {
      errorResolver({ error, setNotification });
    } finally {
      setWaiting(false);
    }
  };
  const handleShowToken = (id: number) => {
    setOpenShowTokenModal?.(true);
    setTokenId?.(id);
  };
  const getDataTransaction = async () => {
    try {
      setWaiting(true);
      const walletListRequest = new AccessTokensListRequest(protectedInstance);
      const resp = await walletListRequest.list({
        page,
        count: rowsPerPage,
        ...parseQueryString(),
      });
      setTotal(resp.data.total);
      const transactions = resp.data.access_tokens.map(
        ({ created_at, expired_at, workspace, pbx_server_id, id }) => ({
          id,
          created_at,
          workspace: workspace && (
            <UserLink
              disabled={!can(AccessEnums.AdminWorkspacesList)}
              user={{ id: workspace.id, mobile: workspace?.name }}
              link={queryStringUrl(routerPaths.WORKSPACES, {
                filters: {
                  [WorkspaceListColumnFilterable.ID]: {
                    column: WorkspaceListColumnFilterable.ID,
                    operator: FilterOperatorEnum.EQUAL,
                    value: workspace?.id,
                  },
                },
                sorts: {
                  [WorkspaceListColumnSortable.ID]: {
                    column: WorkspaceListColumnSortable.ID,
                    direction: SortDirectionEnum.DESC,
                  },
                },
              })}
            />
          ),
          expired_at,
          pbx_server_id: pbx_server_id && (
            <UserLink
              disabled={!can(AccessEnums.AdminPartnerList)}
              user={{ id: pbx_server_id}}
              link={queryStringUrl(routerPaths.SERVERS, {
                filters: {
                  [PartnerListColumnFilterable.ID]: {
                    column: PartnerListColumnFilterable.ID,
                    operator: FilterOperatorEnum.EQUAL,
                    value: pbx_server_id,
                  },
                },
                sorts: {
                  [PartnerListColumnSortable.ID]: {
                    column: PartnerListColumnSortable.ID,
                    direction: SortDirectionEnum.DESC,
                  },
                },
              })}
            />
          ),
          operations: (
            <Stack
              justifyContent="center"
              gap="3px"
              alignItems="center"
              direction="row"
            >
              <LinkWithPopover
                disabled={!can(AccessEnums.AdminShowAccessToken)}
                title="توکن"
              >
                <IconButton
                  sx={{ color: "#3C9A4F" }}
                  onClick={() => handleShowToken(id)}
                >
                  <Key size="18" />
                </IconButton>
              </LinkWithPopover>
              <Stack
                sx={{
                  padding: "4px",
                  borderRadius: "50%",
                  width: "fit-content",
                  cursor: "pointer",
                  color: (theme) => theme.palette.error.main,
                  "&:hover": {
                    color: (theme) => theme.palette.error.dark,
                  },
                }}
                onClick={() => handleDelete(id)}
              >
                <Trash size="20" />
              </Stack>
              <LinkWithPopover
                title="لاگ ها"
                disabled={!can(AccessEnums.AdminLogsList)}
              >
                <Stack
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    width: "fit-content",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                      // backgroundColor: (theme) => theme.palette.others.doneBg,
                    },
                  }}
                  component={Link}
                  to={queryStringUrl(routerPaths.LOGS, {
                    filters: {
                      [LogListColumnFilterable.ACCESS_TOKEN_ID]: {
                        column: LogListColumnFilterable.ACCESS_TOKEN_ID,
                        operator: FilterOperatorEnum.EQUAL,
                        value: id,
                      },
                    },
                    sorts: {
                      [LogListColumnSortable.ID]: {
                        column: LogListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <DocumentText size="20" />
                </Stack>
              </LinkWithPopover>
            </Stack>
          ),
        })
      );
      setDataTransaction(transactions);
      setWaiting(false);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  useEffect(() => {
    getDataTransaction();
  }, [walletReload, page, rowsPerPage, location.search]);

  const _columns: ITableSchema[] = [
    {
      key: "id",
      label: "شناسه",
      minWidth: 100,
      align: "center",
      FilterComponent: NumberFilter,
      sortable: true,
    },
    {
      key: "created_at",
      label: "ایجاد",
      minWidth: 170,
      align: "center",
      FilterComponent: DateTimeFilter,
      format: (value) => value && dateFormatter({ lang: "fa", date: value }),
    },
    {
      key: "expired_at",
      label: "انقضا",
      minWidth: 170,
      align: "center",
      FilterComponent: DateTimeFilter,
      format: (value) => value && dateFormatter({ lang: "fa", date: value }),
    },
    {
      key: "pbx_server_id",
      label: "سرور مرکز تماس",
      minWidth: 170,
      align: "center",
    },
    {
      key: "workspace",
      label: "تخته",
      minWidth: 170,
      align: "center",
    },
    {
      key: "operations",
      label: "عملیات",
      minWidth: 170,
      align: "center",
    },
  ];
  const { filtersSchema, columns } = useTableSchema(_columns);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setParamWithSearchParams("page", String(newPage));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRows = +event.target.value;
    setRowsPerPage(newRows);
    setParamWithSearchParams("count", String(newRows));
  };

  return (
    <Box sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}>
      <Box
        sx={{ borderBottom: 1, borderColor: "divider", width: "100%" }}
      ></Box>

      <CustomTable
        reloadAction={() => setWalletReload(!walletReload)}
        hasWaiting={waiting}
        hasSearchFilter
        hasBottomPagination
        hasTopPagination
        columns={columns}
        count={total}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        page={page}
        rows={dataTransaction}
        rowsPerPage={rowsPerPage}
        filtersSchema={filtersSchema}
        onApplySorts={applyUrlSorts}
        sorts={sorts}
      />
      {/* <UserEditModal
        onCloseModal={() => setOpenEditModal(false)}
        openModal={openEditModal}
        userInfo={openEditModal != false ? openEditModal : null}
      /> */}
    </Box>
  );
}
